/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { frame } from '../flight/state.ts'
import { makeDocumentAtlas } from '../assets/documents.ts'
import { loadTexture } from '../assets/loaders.ts'
import { HASH } from '../shaders/chunks.ts'
import { mulberry32 } from '../prng.ts'
import { Q } from '../quality.ts'

// FOUNDATION §6.5 — one instanced pool of paper. Three stateless roles, all f(uniforms):
//   litter  — always-on sheets on the floor (HOLD/CREATE drifts of paper)
//   rising  — ARTIFACTS: sheets lift off the desks as glyphs are consumed
//   locked  — ARCHITECTURE: the same sheets assemble into walls/columns/vault + corridor
// A sheet that becomes architecture is the same instance, relocated (SPEC §5).

const VERT = /* glsl */ `
attribute float aSeed;
attribute float aIndex;
uniform float uTime, uCount, uLift, uLock, uFog, uPool;
uniform vec3 uBg;
varying vec2 vUv;
varying float vFo;
varying float vVis;
varying float vLight;
${HASH}
mat3 rotY(float a){ float c=cos(a),s=sin(a); return mat3(c,0.0,s, 0.0,1.0,0.0, -s,0.0,c); }
mat3 rotX(float a){ float c=cos(a),s=sin(a); return mat3(1.0,0.0,0.0, 0.0,c,s, 0.0,-s,c); }
void main() {
  vec3 r = hash31(aSeed);
  vec3 r2 = hash31(aSeed + 5.5);
  float cellI = floor(r2.z * 15.999);
  vUv = (vec2(mod(cellI, 4.0), floor(cellI / 4.0)) + uv) / 4.0;

  float isLitter = step(aIndex, 79.5);

  // litter: flat on the floor; first sheets near the hero desk, the rest in the aisles
  vec2 litterXZ = mix(
    vec2(2.2, 0.4) + (r.xz - 0.5) * 4.0,
    vec2((r.x * 2.0 - 1.0) * 50.0, -20.0 + r.z * 100.0),
    step(11.5, aIndex)
  );
  vec3 pLit = vec3(litterXZ.x, 0.015 + r2.y * 0.01, litterXZ.y);
  mat3 rLit = rotX(-1.5708) * rotY(r.y * 6.283);

  // rising: lift off the desk field, tumbling, buoyant
  vec2 fieldXZ = vec2((r.x * 2.0 - 1.0) * 45.0, -50.0 + r.z * 126.0);
  float liftS = smoothstep(fract(aSeed * 0.617) * 0.4, 1.0, uLift); // staggered lift
  vec3 pRise = vec3(
    fieldXZ.x + sin(uTime * 0.4 + aSeed) * 0.4,
    0.85 + liftS * (1.5 + r.y * 10.0) + 0.22 * sin(uTime * 0.7 + aSeed * 3.0),
    fieldXZ.y + liftS * 6.0 * (r2.x - 0.3)
  );
  mat3 rRise = rotY(uTime * (0.15 + r.x * 0.3) + aSeed) * rotX(0.9 + 0.5 * sin(uTime * 0.5 + aSeed));

  // locked: architecture — two walls of stacked paper, hanging columns, a partial vault
  float side = sign(r2.x - 0.5);
  float colI = floor(r.x * 30.0);
  float rowI = floor(r.y * 13.0);
  vec3 pArch = vec3(side * 3.6 + (r2.y - 0.5) * 0.25, 0.25 + rowI * 0.5, 87.0 + colI * 0.85);
  mat3 rArch = rotY(1.5708 + (r.z - 0.5) * 0.12);
  // hanging document columns outside the walls
  if (r2.z > 0.68 && r2.z <= 0.84) {
    pArch = vec3(side * (7.0 + r.x * 3.0), 1.0 + rowI * 0.45, 84.0 + colI * 1.1);
    rArch = rotY((r.z - 0.5) * 0.4);
  }
  // vault slabs overhead, angled inward
  if (r2.z > 0.84) {
    pArch = vec3(side * (1.1 + r.x * 2.2), 6.5 + r2.y * 0.6, 87.0 + colI * 0.85);
    rArch = rotX(-1.5708 + side * 0.5) * rotY(r.y * 0.3);
  }

  float lockS = smoothstep(fract(aSeed * 0.731) * 0.5, fract(aSeed * 0.731) * 0.5 + 0.5, uLock);
  vec3 pos = mix(pRise, pArch, lockS);
  mat3 R = rLit; // litter default
  // note: matrices can't lerp cleanly; crossfade orientation by choosing rise/arch and
  // letting position carry the transition — reads as sheets settling into place
  mat3 rDyn = lockS > 0.5 ? rArch : rRise;
  pos = mix(pos, pLit, isLitter);

  float act = isLitter + (1.0 - isLitter) * step(aIndex / uPool, uCount);
  // rising/architecture sheets are oversized so they read at 40m+; floor litter is
  // near the camera at HOLD and stays close to true A4
  vec2 paper = vec2(0.21, 0.297) * (0.85 + r2.y * 0.5) * mix(3.2, 1.25, isLitter);
  vec3 local = vec3(position.x * paper.x, position.y * paper.y, 0.0);
  // flutter, dies as sheets lock
  local.z += sin(uv.x * 6.283 + uTime * 2.2 + aSeed * 7.0) * 0.03 * (1.0 - lockS) * (1.0 - isLitter);
  mat3 RR = isLitter > 0.5 ? rLit : rDyn;
  vec3 wp = RR * local * act + pos;

  vec4 mv = viewMatrix * vec4(wp, 1.0);
  gl_Position = projectionMatrix * mv;
  float dist = length(mv.xyz);
  vFo = 1.0 - exp(-uFog * uFog * dist * dist);
  vVis = act;
  vec3 n = normalize(RR * vec3(0.0, 0.0, 1.0));
  vLight = 0.86 + 0.14 * abs(dot(n, normalize(vec3(0.35, 1.0, 0.5))));
}
`

const FRAG = /* glsl */ `
uniform sampler2D uTex;
uniform vec3 uBg;
uniform float uChroma;
varying vec2 vUv;
varying float vFo;
varying float vVis;
varying float vLight;
void main() {
  if (vVis < 0.5) discard;
  vec3 col = texture2D(uTex, vUv).rgb * vLight;
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  // the documents atlas carries cyan chart marks — contained colour obeys the
  // chroma curve like everything else (law 1)
  col = mix(vec3(lum), col, clamp(uChroma + 0.35, 0.0, 1.0));
  col = mix(col, vec3(lum), clamp(vFo * 1.7, 0.0, 1.0));
  col = mix(col, uBg, vFo);
  gl_FragColor = vec4(col, 1.0);
}
`

export function Sheets() {
  const { geometry, material } = useMemo(() => {
    const N = Q.sheets
    const base = new THREE.PlaneGeometry(1, 1, 4, 1) // segments for the flutter wave
    const geometry = new THREE.InstancedBufferGeometry()
    geometry.index = base.index
    geometry.setAttribute('position', base.attributes.position)
    geometry.setAttribute('uv', base.attributes.uv)
    const rand = mulberry32(5150)
    const seeds = new Float32Array(N)
    const idx = new Float32Array(N)
    for (let i = 0; i < N; i++) {
      seeds[i] = rand() * 120
      idx[i] = i
    }
    geometry.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 1))
    geometry.setAttribute('aIndex', new THREE.InstancedBufferAttribute(idx, 1))
    geometry.instanceCount = N

    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      side: THREE.DoubleSide,
      uniforms: {
        uTex: { value: loadTexture('/assets/documents.png', makeDocumentAtlas) },
        uTime: { value: 0 },
        uCount: { value: 0 },
        uLift: { value: 0 },
        uLock: { value: 0 },
        uFog: { value: 0.016 },
        uChroma: { value: 0 },
        uPool: { value: N },
        uBg: { value: new THREE.Color() },
      },
    })
    return { geometry, material }
  }, [])

  useFrame((st) => {
    const b = frame.beats
    const u = material.uniforms
    u.uTime.value = st.clock.elapsedTime
    u.uCount.value = b.sheets
    u.uLift.value = b.lift
    u.uLock.value = b.lock
    u.uFog.value = b.fog
    u.uChroma.value = b.chroma
    ;(u.uBg.value as THREE.Color).copy(frame.bg)
  })

  return <mesh geometry={geometry} material={material} frustumCulled={false} />
}
